import { createSelector, createSlice } from "@reduxjs/toolkit"
import { notifiesService } from "../../services/book/notifies"
import { leadNotifiesService } from "../../services/book/leadNotifies"
import { aiChatBotService } from "../../services/book/aiChatBot"
import { normalizeMessages } from "../../../packs/widgets/utils/parsers/messages"
import { mapToSelectNamesOptions } from "../../../shared/parsers/options"

const initialState = {
  data: {
    list: [],
    meta: {},
  },
  meta: {},
  statesOptions: [],
  departmentsOptions: [],
}

const bumpMetaCounter = (state, key, direction) => {
  const current = state.meta[key] ?? 0
  state.meta[key] = Math.max(0, current + direction)
}

const notifiesSlice = createSlice({
  name: "notifies",
  initialState,
  reducers: {
    resetNotifies: (state) => {
      state.data.list = []
      state.data.meta = {}
    },
    setNotifies: (state, action) => {
      const { items, meta } = action.payload
      const { current_page, total_pages, total_entries, per_page } = meta

      state.data.list = normalizeMessages(items)
      state.data.meta = {
        count: total_entries,
        page: current_page,
        total_pages,
        per_page,
        hasMore: current_page < total_pages,
      }
    },
    markChatAsRead: (state, action) => {
      const { bundle_id } = action.payload
      const chat = state.data.list.find((c) => c.bundle_id === bundle_id)
      if (chat) {
        const wasUnread = (chat.unread_count ?? 0) > 0
        chat.unread_count = 0
        chat.is_read = true
        if (wasUnread) {
          const metaKey = chat.lead_id != null ? "leads_unread" : "total_unread"
          bumpMetaCounter(state, metaKey, -1)
        }
      }
    },
    bumpChat: (state, action) => {
      const { bundle_id, lead_id, message, created_at, extra } = action.payload
      const index = state.data.list.findIndex(
        (c) =>
          (bundle_id != null && c.bundle_id === bundle_id) ||
          (lead_id != null && c.lead_id === lead_id)
      )

      if (index === -1) {
        // чата ещё нет в загруженном списке — добавляем новую запись сверху
        state.data.list.unshift({
          bundle_id: bundle_id ?? null,
          lead_id: lead_id ?? null,
          message: message ?? "",
          created_at: created_at ?? new Date().toISOString(),
          unread_count: 0,
          ...extra,
        })
        return
      }

      const chat = { ...state.data.list[index] }
      if (message !== undefined) chat.message = message
      if (created_at !== undefined) chat.created_at = created_at

      state.data.list.splice(index, 1)
      state.data.list.unshift(chat)
    },
    adjustUnreadCount: (state, action) => {
      const { bundle_id, lead_id, delta } = action.payload
      const chat = state.data.list.find(
        (c) =>
          (bundle_id != null && c.bundle_id === bundle_id) ||
          (lead_id != null && c.lead_id === lead_id)
      )
      if (!chat) return

      const wasUnread = (chat.unread_count ?? 0) > 0
      chat.unread_count = Math.max(0, (chat.unread_count ?? 0) + delta)
      const isNowUnread = chat.unread_count > 0

      if (wasUnread !== isNowUnread) {
        const metaKey = lead_id != null ? "leads_unread" : "total_unread"
        bumpMetaCounter(state, metaKey, isNowUnread ? 1 : -1)
      }
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      notifiesService.endpoints.getChatNotifies.matchFulfilled,
      (state, action) => {
        if (!action.payload) return
        const { data: items, meta } = action.payload
        const { current_page, total_pages, total_entries, per_page } = meta
        const normalized = normalizeMessages(items)

        if (current_page === 1) {
          state.data.list = normalized
        } else {
          state.data.list = [...state.data.list, ...normalized]
        }

        state.data.meta = {
          count: total_entries,
          page: current_page,
          total_pages,
          per_page,
          hasMore: current_page < total_pages,
        }
      }
    )

    builder.addMatcher(notifiesService.endpoints.getChatMeta.matchFulfilled, (state, action) => {
      const { states, departments } = action.payload

      // leads_unread приходит с бэка в том же ответе
      state.meta = action.payload

      if (states && Array.isArray(states)) {
        state.statesOptions = mapToSelectNamesOptions(states)
      }

      if (departments && Array.isArray(departments)) {
        state.departmentsOptions = departments.map((item) => ({
          key: item.id,
          value: item.name,
        }))
      }
    })

    builder.addMatcher(
      notifiesService.endpoints.getListFlightChanges.matchFulfilled,
      (state, action) => {
        if (!action.payload) return
        const { data: items, meta } = action.payload
        const { current_page, total_pages, total_entries, per_page } = meta
        const normalized = normalizeMessages(items)

        if (current_page === 1) {
          state.data.list = normalized
        } else {
          state.data.list = [...state.data.list, ...normalized]
        }

        state.data.meta = {
          count: total_entries,
          page: current_page,
          total_pages,
          per_page,
          hasMore: current_page < total_pages,
        }
      }
    )

    builder.addMatcher(
      leadNotifiesService.endpoints.getLeadNotifies.matchFulfilled,
      (state, action) => {
        if (!action.payload) return
        const { data: items, meta } = action.payload
        const { current_page, total_pages, total_entries, per_page } = meta
        const normalized = normalizeMessages(items)

        if (current_page === 1) {
          state.data.list = normalized
        } else {
          state.data.list = [...state.data.list, ...normalized]
        }

        state.data.meta = {
          count: total_entries,
          page: current_page,
          total_pages,
          per_page,
          hasMore: current_page < total_pages,
        }
      }
    )

    // ── AI Bot conversations list ────────────────────────────────────────────
    builder.addMatcher(
      aiChatBotService.endpoints.getAiChatBotConversations.matchFulfilled,
      (state, action) => {
        if (!action.payload) return
        const { data: items, meta } = action.payload
        const { current_page, total_pages, total_entries, per_page } = meta
        const normalized = normalizeMessages(items)

        if (current_page === 1) {
          state.data.list = normalized
        } else {
          state.data.list = [...state.data.list, ...normalized]
        }

        state.data.meta = {
          count: total_entries,
          page: current_page,
          total_pages,
          per_page,
          hasMore: current_page < total_pages,
        }
      }
    )
  },
})

export const { resetNotifies, setNotifies, markChatAsRead, bumpChat, adjustUnreadCount } =
  notifiesSlice.actions

const selectNotifiesState = (state) => state.notifies

export const selectNotifiesList = createSelector([selectNotifiesState], (state) => state.data)

export const selectNotifiesHasMore = createSelector(
  [selectNotifiesState],
  (state) => state.data.meta.hasMore ?? false
)

export const selectNotifiesPage = createSelector(
  [selectNotifiesState],
  (state) => state.data.meta.page ?? 1
)

export const selectNotifiesChatMeta = createSelector([selectNotifiesState], (state) => state.meta)

export const selectNotifiesStatesOptions = createSelector(
  [selectNotifiesState],
  (state) => state.statesOptions
)

export const selectNotifiesDeparmentsOptions = createSelector(
  [selectNotifiesState],
  (state) => state.departmentsOptions
)

export default notifiesSlice.reducer
